import { useState, useCallback, useRef, type ReactNode, type ChangeEvent } from 'react'
import { Users, HeartHandshake, Target, BookOpen, Heart, Camera, Loader2 } from 'lucide-react'
import { useProfile } from '@/hooks/useProfile'
import { changelog, LAST_SEEN_VERSION_KEY } from '@/changelog'
import { AvatarCropper, analyzeImage, autoCenterCrop } from '@/components/AvatarCropper'
import LogoWordmark from '@/components/LogoWordmark'

/** Clé partagée avec useNotifications pour la synchronisation rapide */
export const NOTIF_LS_KEY = 'selah_notif_enabled'

// ─── Constantes ──────────────────────────────────────────────────────────────

/** Nombre total d'écrans (6 d'info + 1 de configuration) */
const TOTAL = 7
/** Distance (px) du swipe horizontal qui valide le changement de slide */
const SWIPE_THRESHOLD = 50
/** Distance avant verrouillage de la direction horizontale/verticale */
const DIRECTION_LOCK = 12

// ─── Types ───────────────────────────────────────────────────────────────────

interface Props {
  onComplete: () => void
}

interface SlideContentProps {
  step: number
  total: number
  icon: ReactNode
  title: string
  desc: string
}

// ─── Indicateur de progression ───────────────────────────────────────────────

function ProgressDots({ step, total, onJump }: { step: number; total: number; onJump?: (i: number) => void }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          onClick={() => onJump?.(i)}
          aria-label={`Aller à l'étape ${i + 1}`}
          style={{
            width: i === step ? 22 : 7,
            height: 7,
            borderRadius: 4,
            backgroundColor: i === step ? 'var(--selah-text)' : 'var(--selah-border)',
            transition: 'width 0.3s ease, background-color 0.3s ease',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        />
      ))}
    </div>
  )
}

// ─── Cadre d'icône standard ──────────────────────────────────────────────────

function IconFrame({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--selah-card)',
        border: '1px solid var(--selah-border)',
        borderRadius: 24,
        padding: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  )
}

// ─── Slide d'information (écrans 1 à 6) ──────────────────────────────────────

function InfoSlide({ step, total, icon, title, desc }: SlideContentProps) {
  return (
    <div
      style={{
        width: `${100 / TOTAL}%`,
        height: '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 24px',
        gap: 24,
        textAlign: 'center',
      }}
    >
      <IconFrame>{icon}</IconFrame>
      <ProgressDots step={step} total={total} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
        <h2
          style={{
            fontWeight: 600,
            fontSize: 24,
            lineHeight: 1.2,
            color: 'var(--selah-text)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h2>
        <p
          style={{
            fontWeight: 400,
            fontSize: 15,
            lineHeight: 1.6,
            color: 'var(--selah-text-secondary)',
            maxWidth: 300,
            margin: 0,
          }}
        >
          {desc}
        </p>
      </div>
    </div>
  )
}

// ─── Slide de configuration (écran 7) ────────────────────────────────────────

interface ConfigSlideProps {
  step: number
  total: number
  firstName: string
  setFirstName: (v: string) => void
  avatarPreview: string | null
  onPickFile: (e: ChangeEvent<HTMLInputElement>) => void
  fileInputRef: React.RefObject<HTMLInputElement>
  dailyTime: string
  setDailyTime: (v: string) => void
}

function ConfigSlide({
  step, total, firstName, setFirstName, avatarPreview, onPickFile, fileInputRef, dailyTime, setDailyTime,
}: ConfigSlideProps) {
  return (
    <div
      style={{
        width: `${100 / TOTAL}%`,
        height: '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 24px 0',
        gap: 24,
      }}
    >
      <h2
        style={{
          fontWeight: 600,
          fontSize: 24,
          lineHeight: 1.2,
          color: 'var(--selah-text)',
          textAlign: 'center',
          margin: 0,
          letterSpacing: '-0.01em',
        }}
      >
        Personnalise ton expérience
      </h2>

      <ProgressDots step={step} total={total} />

      {/* Photo de profil */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Choisir une photo"
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'var(--selah-card)',
            border: '1px solid var(--selah-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            cursor: 'pointer',
            padding: 0,
            position: 'relative',
          }}
        >
          {avatarPreview ? (
            <img src={avatarPreview} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Camera size={28} style={{ color: 'var(--selah-text-secondary)' }} />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          style={{ display: 'none' }}
        />
      </div>

      {/* Prénom */}
      <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--selah-text-secondary)',
            paddingLeft: 4,
          }}
        >
          Ton prénom *
        </label>
        <input
          type="text"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          placeholder="Ton prénom"
          autoCapitalize="words"
          autoComplete="given-name"
          style={{
            width: '100%',
            backgroundColor: 'var(--selah-input-bg)',
            border: '1px solid var(--selah-input-border)',
            borderRadius: 12,
            padding: '12px 16px',
            fontSize: 16, // 16px évite le zoom auto iOS Safari
            color: 'var(--selah-text)',
            outline: 'none',
          }}
        />
      </div>

      {/* Sélecteur d'heure */}
      <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--selah-text-secondary)',
            paddingLeft: 4,
          }}
        >
          À quelle heure veux-tu ton moment Selah ?
        </label>
        <input
          type="time"
          value={dailyTime}
          onChange={e => setDailyTime(e.target.value)}
          style={{
            width: '100%',
            backgroundColor: 'var(--selah-input-bg)',
            border: '1px solid var(--selah-input-border)',
            borderRadius: 12,
            padding: '12px 16px',
            fontSize: 16,
            color: 'var(--selah-text)',
            outline: 'none',
          }}
        />
      </div>
    </div>
  )
}

// ─── Composant principal ─────────────────────────────────────────────────────

export default function OnboardingFlow({ onComplete }: Props) {
  const { profile, updateProfile, uploadAvatar } = useProfile()

  const [step, setStep] = useState(0)
  const [firstName, setFirstName] = useState(profile?.first_name ?? '')
  const [dailyTime, setDailyTime] = useState(profile?.daily_time ?? '08:00')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile?.avatar_url ?? null)
  const [pickedBlob, setPickedBlob] = useState<Blob | null>(null)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [firstNameError, setFirstNameError] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const isLast = step === TOTAL - 1
  const isConfig = isLast

  // ── Navigation ─────────────────────────────────────────────────────────────

  const goTo = useCallback((i: number) => {
    setStep(Math.max(0, Math.min(TOTAL - 1, i)))
  }, [])

  const next = useCallback(() => {
    setStep(s => Math.min(s + 1, TOTAL - 1))
  }, [])

  const skip = useCallback(() => {
    // Marque la version courante comme vue → pas de doublon WhatsNewModal après l'onboarding
    try { localStorage.setItem(LAST_SEEN_VERSION_KEY, changelog.version) } catch { /* ignore */ }
    onComplete()
  }, [onComplete])

  // ── Sélection d'avatar (preview locale uniquement) ─────────────────────────

  // ── Sélection d'avatar : analyse ratio → crop auto ou ouverture cropper ──
  const applyBlob = useCallback((blob: Blob) => {
    setPickedBlob(blob)
    const url = URL.createObjectURL(blob)
    setAvatarPreview(url)
  }, [])

  const handlePickFile = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (e.target) e.target.value = ''
    if (!file) return
    try {
      const analysis = await analyzeImage(file)
      if (analysis.tooSmall) {
        setSubmitError('Image trop petite, choisis une photo de meilleure qualité.')
        return
      }
      setSubmitError(null)
      if (analysis.isSquare) {
        const blob = await autoCenterCrop(file)
        applyBlob(blob)
        return
      }
      setCropFile(file)
    } catch {
      setSubmitError('Impossible de lire cette image. Choisis un autre fichier.')
    }
  }, [applyBlob])

  // ── Submit final ───────────────────────────────────────────────────────────

  const handleStart = useCallback(async () => {
    if (!firstName.trim()) {
      setFirstNameError(true)
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      // 1. Sauvegarde prénom + heure du moment quotidien
      await updateProfile({ first_name: firstName.trim(), daily_time: dailyTime })

      // 2. Upload avatar si choisi (déjà cropé en carré par le cropper)
      if (pickedBlob) {
        const result = await uploadAvatar(pickedBlob)
        if (result.error) {
          // Non bloquant : on ferme l'onboarding même si l'avatar a échoué
          if (!import.meta.env.PROD) console.warn('[Onboarding] avatar upload failed:', result.error)
        }
      }

      // 3. Marquer la version courante comme vue → pas de WhatsNewModal en doublon
      try { localStorage.setItem(LAST_SEEN_VERSION_KEY, changelog.version) } catch { /* ignore */ }

      onComplete()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Une erreur est survenue.')
    } finally {
      setSubmitting(false)
    }
  }, [firstName, dailyTime, pickedBlob, updateProfile, uploadAvatar, onComplete])

  // ── Swipe horizontal ───────────────────────────────────────────────────────

  const startX = useRef<number | null>(null)
  const startY = useRef<number | null>(null)
  const isHorizontal = useRef<boolean | null>(null)

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    if (!t) return
    startX.current = t.clientX
    startY.current = t.clientY
    isHorizontal.current = null
  }

  const onTouchMove = (e: React.TouchEvent) => {
    if (startX.current === null || startY.current === null) return
    const t = e.touches[0]
    if (!t) return
    const dx = t.clientX - startX.current
    const dy = t.clientY - startY.current
    if (isHorizontal.current === null) {
      if (Math.abs(dx) >= DIRECTION_LOCK || Math.abs(dy) >= DIRECTION_LOCK) {
        isHorizontal.current = Math.abs(dx) > Math.abs(dy)
        if (!isHorizontal.current) startX.current = null
      }
    }
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current === null) return
    const endX = e.changedTouches[0]?.clientX ?? startX.current
    const dx = endX - startX.current
    startX.current = null
    startY.current = null
    if (!isHorizontal.current) return
    if (dx > SWIPE_THRESHOLD) goTo(step - 1)
    else if (dx < -SWIPE_THRESHOLD) goTo(step + 1)
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: 'var(--selah-bg)',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* ── Track des slides ── */}
      <div
        style={{ flex: 1, overflow: 'hidden', position: 'relative' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          style={{
            display: 'flex',
            width: `${TOTAL * 100}%`,
            height: '100%',
            transform: `translateX(-${step * (100 / TOTAL)}%)`,
            transition: 'transform 0.3s ease-out',
          }}
        >
          {/* Écran 1 — Bienvenue */}
          <InfoSlide
            step={step}
            total={TOTAL}
            icon={
              <LogoWordmark size={48} />
            }
            title="Bienvenue sur Selah"
            desc="Ton espace personnel pour prier, méditer et grandir spirituellement. Chaque jour, un pas de plus avec Dieu."
          />

          {/* Écran 2 — Proches */}
          <InfoSlide
            step={step}
            total={TOTAL}
            icon={<Users size={48} strokeWidth={1.5} style={{ color: 'var(--selah-text)' }} />}
            title="Prie avec tes proches"
            desc="Connecte-toi avec tes amis grâce à ton code Selah. Partagez vos prières, soutenez-vous mutuellement et célébrez les exaucements ensemble."
          />

          {/* Écran 3 — Prière */}
          <InfoSlide
            step={step}
            total={TOTAL}
            icon={<HeartHandshake size={48} strokeWidth={1.5} style={{ color: 'var(--selah-text)' }} />}
            title="Ta vie de prière"
            desc="Note tes sujets de prière et suis les exaucements. Programme des prières récurrentes pour ne rien oublier et laisse tes proches intercéder pour toi."
          />

          {/* Écran 4 — Objectifs */}
          <InfoSlide
            step={step}
            total={TOTAL}
            icon={<Target size={48} strokeWidth={1.5} style={{ color: 'var(--selah-text)' }} />}
            title="Fixe-toi des objectifs"
            desc="Définis tes objectifs spirituels, active le suivi quotidien et fais ton check-in chaque soir. Partage-les avec tes proches pour avancer ensemble."
          />

          {/* Écran 5 — Versets */}
          <InfoSlide
            step={step}
            total={TOTAL}
            icon={<BookOpen size={48} strokeWidth={1.5} style={{ color: 'var(--selah-text)' }} />}
            title="Mémorise et médite"
            desc="Enregistre les versets qui te parlent et révise-les régulièrement. Utilise le journal dévotionnel pour approfondir ta méditation : Écriture, Observation, Application, Prière."
          />

          {/* Écran 6 — Don */}
          <InfoSlide
            step={step}
            total={TOTAL}
            icon={<Heart size={48} strokeWidth={1.5} style={{ color: 'var(--selah-text)' }} />}
            title="Soutiens Selah"
            desc="Selah est une app gratuite développée avec passion par un développeur indépendant. Si l'app te bénit, tu peux soutenir son développement par un don."
          />

          {/* Écran 7 — Configuration */}
          <ConfigSlide
            step={step}
            total={TOTAL}
            firstName={firstName}
            setFirstName={(v) => { setFirstName(v); if (firstNameError) setFirstNameError(false) }}
            avatarPreview={avatarPreview}
            onPickFile={handlePickFile}
            fileInputRef={fileInputRef}
            dailyTime={dailyTime}
            setDailyTime={setDailyTime}
          />
        </div>
      </div>

      {/* ── Erreurs (config) ── */}
      {isConfig && (firstNameError || submitError) && (
        <div style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 8, textAlign: 'center' }}>
          {firstNameError && (
            <p
              style={{
                fontSize: 13,
                color: 'var(--selah-error)',
                margin: 0,
              }}
            >
              Ton prénom est requis pour continuer.
            </p>
          )}
          {submitError && (
            <p
              style={{
                fontSize: 13,
                color: 'var(--selah-error)',
                margin: 0,
              }}
            >
              {submitError}
            </p>
          )}
        </div>
      )}

      {/* ── Contrôles bas de page ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '20px 24px 32px',
        }}
      >
        {/* Passer (gauche) — masqué sur l'écran de configuration */}
        {!isConfig ? (
          <button
            onClick={skip}
            style={{
              minHeight: 44,
              minWidth: 44,
              padding: '10px 12px',
              background: 'none',
              border: 'none',
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--selah-text-secondary)',
              cursor: 'pointer',
            }}
          >
            Passer
          </button>
        ) : <span />}

        {/* Suivant / Commencer (droite) */}
        {isConfig ? (
          <button
            onClick={handleStart}
            disabled={submitting || !firstName.trim()}
            style={{
              flex: 1,
              minHeight: 48,
              padding: '12px 32px',
              borderRadius: 24,
              backgroundColor: 'var(--selah-button-bg)',
              color: 'var(--selah-button-text)',
              border: 'none',
              fontSize: 16,
              fontWeight: 600,
              cursor: submitting ? 'wait' : 'pointer',
              opacity: !firstName.trim() ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? 'Enregistrement…' : 'Commencer'}
          </button>
        ) : (
          <button
            onClick={isLast ? skip : next}
            style={{
              minHeight: 44,
              padding: '12px 32px',
              borderRadius: 24,
              backgroundColor: 'var(--selah-button-bg)',
              color: 'var(--selah-button-text)',
              border: 'none',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Suivant
          </button>
        )}
      </div>

      {/* Avatar cropper — s'ouvre quand une image non carrée est sélectionnée */}
      {cropFile && (
        <AvatarCropper
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onConfirm={(blob) => {
            setCropFile(null)
            applyBlob(blob)
          }}
        />
      )}
    </div>
  )
}
